import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion';
import {
  User as UserIcon,
  Mail,
  Briefcase,
  ShieldCheck,
  CalendarDays,
  Save,
  Loader2,
  RefreshCw,
  Building2,
  Globe,
  MapPin,
  FileText,
  Hash,
  Coins,
  Layers,
  Lock,
  Trash2,
} from 'lucide-react';
import {
  initialRecruiterResponse,
  RecruiterResponse,
} from '../Types/RecruiterTypes';
import { getRecuiterById, updateRecuiterById } from '../Services/RecruiterService';
import {
  initialCompanyResponse,
  CompanyResponse,
  CompanyRequest,
} from '../../Company/Types/CompanyType';
import { getCompanyById, updateCompany } from '../../Company/Services/CompanyService';
import { useFormController } from '../../Shared/Component/readFormData/useFormController';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import { formatDateTime } from '../../Shared/Utils/HelperUtils';
import AccountAccessPanel from '../../Shared/Component/AccountAccessPanel';

const inputClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-100 placeholder-slate-600 outline-none transition focus:border-cyan-500/50 disabled:cursor-not-allowed disabled:opacity-60';

const Field = ({
  label,
  icon,
  children,
}: {
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <label className="block">
    <span className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-slate-400">
      {icon}
      {label}
    </span>
    {children}
  </label>
);

const SectionCard = ({
  icon,
  title,
  subtitle,
  action,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 sm:p-6">
    <div className="mb-5 flex items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
          {icon}
        </span>
        <div>
          <h2 className="text-sm font-black tracking-tight text-slate-100 sm:text-base">{title}</h2>
          {subtitle && <p className="text-[11px] font-semibold text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
    {children}
  </div>
);

function RecruiterProfile() {
  const recruiterForm = useFormController<RecruiterResponse>(initialRecruiterResponse);
  const companyForm = useFormController<CompanyResponse>(initialCompanyResponse);

  const {
    values,
    setAllValues,
    handleInputChange,
  } = recruiterForm;

  const {
    values: companyValues,
    setAllValues: setCompanyValues,
    handleInputChange: handleCompanyInputChange,
  } = companyForm;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);

  const canEditCompany = Boolean(values.isCompanyAdminUser);
  const fullName =
    `${values.recruiterFirstName ?? ''} ${values.recruiterLastName ?? ''}`.trim() || 'Recruiter';
  const initials =
    `${values.recruiterFirstName?.[0] ?? ''}${values.recruiterLastName?.[0] ?? ''}`.toUpperCase() ||
    'R';

  const showAlert = (variant: AlertProps['variant'], title: string, message: string) =>
    setAlert({ variant, title, message, autoCloseMs: 5000, onClose: () => setAlert(null) });

  const fetchProfile = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getRecuiterById();
      if (response.success && response.data) {
        setAllValues(response.data as RecruiterResponse);

        if (response.data.companyId) {
          const companyResponse = await getCompanyById(response.data.companyId);
          if (companyResponse.success && companyResponse.data) {
            setCompanyValues(companyResponse.data);
          } else {
            console.warn('Company details unavailable:', companyResponse.message);
          }
        }
      } else {
        setError(response.message || 'Failed to load recruiter profile');
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load recruiter profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveProfile = async () => {
    setAlert(null);
    setSavingProfile(true);
    try {
      const response = await updateRecuiterById({
        userId: values.userId,
        companyId: values.companyId,
        designation: values.designation,
        isCompanyAdminUser: values.isCompanyAdminUser,
        recruiterFirstName: values.recruiterFirstName,
        recruiterLastName: values.recruiterLastName,
        recruiterEmail: values.recruiterEmail,
        recruiterStatus: values.recruiterStatus,
      });

      if (response.success && response.data) {
        setAllValues(response.data as RecruiterResponse);
        showAlert('success', 'Profile updated', response.message || 'Your profile was saved successfully.');
      } else {
        showAlert('error', 'Update failed', response.message || 'Could not update your profile.');
      }
    } catch (err) {
      console.error(err);
      showAlert('error', 'System error', 'Something went wrong. Please try again.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveCompany = async () => {
    if (!companyValues.id) return;
    setAlert(null);
    setSavingCompany(true);
    try {
      const payload: CompanyRequest = {
        recruiterId: companyValues.recruiterId,
        companyName: companyValues.companyName,
        companyDomain: companyValues.companyDomain,
        companyLogoUrl: companyValues.companyLogoUrl,
        industry: companyValues.industry,
        companySizeRange: companyValues.companySizeRange,
        website: companyValues.website,
        description: companyValues.description,
        totalCredits: companyValues.totalCredits ?? 0,
        gstNumber: companyValues.gstNumber,
        headquartersLocation: companyValues.headquartersLocation,
      };

      const response = await updateCompany(companyValues.id, payload);
      if (response.success && response.data) {
        setCompanyValues(response.data);
        showAlert('success', 'Company updated', response.message || 'Company details were saved successfully.');
      } else {
        showAlert('error', 'Update failed', response.message || 'Could not update the company details.');
      }
    } catch (err) {
      console.error(err);
      showAlert('error', 'System error', 'Something went wrong. Please try again.');
    } finally {
      setSavingCompany(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center rounded-2xl border border-slate-800 bg-slate-950 p-4">
        <p className="animate-pulse font-mono text-sm tracking-widest text-slate-400">
          LOADING SECURE IDENTITY MODULE...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[400px] items-center justify-center rounded-2xl border border-rose-900/50 bg-slate-950 p-4">
        <p className="font-mono text-sm tracking-wide text-rose-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <div className="mx-auto w-full max-w-5xl space-y-6">
        {alert && <Alert {...alert} />}

        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-5 sm:p-6">
          <div className="absolute left-0 right-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-70" />
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-lg font-black text-cyan-400">
                {initials}
              </span>
              <div className="min-w-0">
                <h1 className="truncate text-lg font-black tracking-tight text-slate-100 sm:text-2xl">
                  {fullName}
                </h1>
                <p className="flex items-center gap-1.5 truncate text-xs font-semibold text-slate-400">
                  <Mail size={12} /> {values.recruiterEmail || '—'}
                </p>
                <p className="mt-1 flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <Briefcase size={11} /> {values.designation || 'Recruiter'}
                  </span>
                  {companyValues.companyName && (
                    <span className="inline-flex items-center gap-1 text-slate-400">
                      <Building2 size={11} /> {companyValues.companyName}
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-950 px-3 py-1.5">
                <span
                  className={`h-2.5 w-2.5 rounded-full ${
                    values.recruiterStatus
                      ? 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]'
                      : 'bg-rose-500'
                  } animate-pulse`}
                />
                <span className="font-mono text-[10px] font-bold tracking-wider text-slate-300">
                  {values.recruiterStatus ? 'ACTIVE' : 'DISABLED'}
                </span>
              </div>
              <button
                onClick={fetchProfile}
                disabled={loading}
                className="flex items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40 disabled:opacity-50"
              >
                <RefreshCw size={15} /> Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Personal information */}
        <SectionCard
          icon={<UserIcon size={18} />}
          title="Personal Information"
          subtitle="Your identity within the company directory"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="First Name" icon={<UserIcon size={12} />}>
              <input
                className={inputClass}
                name="recruiterFirstName"
                value={values.recruiterFirstName ?? ''}
                onChange={handleInputChange('recruiterFirstName')}
              />
            </Field>

            <Field label="Last Name" icon={<UserIcon size={12} />}>
              <input
                className={inputClass}
                name="recruiterLastName"
                value={values.recruiterLastName ?? ''}
                onChange={handleInputChange('recruiterLastName')}
              />
            </Field>

            <Field label="Designation" icon={<Briefcase size={12} />}>
              <input
                className={inputClass}
                name="designation"
                value={values.designation ?? ''}
                onChange={handleInputChange('designation')}
              />
            </Field>

            <Field label="Email" icon={<Mail size={12} />}>
              <input className={inputClass} name="recruiterEmail" value={values.recruiterEmail ?? ''} disabled />
            </Field>
          </div>

          <div className="mt-5 flex items-center justify-between gap-3 border-t border-slate-800/70 pt-5">
            <p className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
              <ShieldCheck size={12} className="text-slate-500" />
              {values.isCompanyAdminUser ? 'Company Admin' : 'Recruiter'} · email managed by your administrator
            </p>
            <motion.button
              onClick={handleSaveProfile}
              disabled={savingProfile}
              whileHover={{ translateY: -1 }}
              whileTap={{ scale: 0.99 }}
              className="flex items-center justify-center gap-2 rounded-xl bg-cyan-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400 disabled:opacity-60"
            >
              {savingProfile ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
              Save Profile
            </motion.button>
          </div>
        </SectionCard>

        {/* Company information */}
        <SectionCard
          icon={<Building2 size={18} />}
          title="Company Information"
          subtitle="Details visible to candidates on your job posts"
          action={
            !canEditCompany ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-800 bg-slate-950 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-slate-400">
                <Lock size={11} /> Admin only
              </span>
            ) : undefined
          }
        >
          {!companyValues.id ? (
            <p className="rounded-xl border border-dashed border-slate-700 bg-slate-950/50 px-4 py-6 text-center text-xs font-semibold text-slate-500">
              No company is linked to your account yet.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label="Company Name" icon={<Building2 size={12} />}>
                  <input
                    className={inputClass}
                    name="companyName"
                    value={companyValues.companyName ?? ''}
                    onChange={handleCompanyInputChange('companyName')}
                    disabled={!canEditCompany}
                  />
                </Field>

                <Field label="Company Domain" icon={<Globe size={12} />}>
                  <input
                    className={inputClass}
                    name="companyDomain"
                    value={companyValues.companyDomain ?? ''}
                    onChange={handleCompanyInputChange('companyDomain')}
                    disabled={!canEditCompany}
                  />
                </Field>

                <Field label="Industry" icon={<Layers size={12} />}>
                  <input
                    className={inputClass}
                    name="industry"
                    value={companyValues.industry ?? ''}
                    onChange={handleCompanyInputChange('industry')}
                    disabled={!canEditCompany}
                  />
                </Field>

                <Field label="Company Size" icon={<UserIcon size={12} />}>
                  <input
                    className={inputClass}
                    name="companySizeRange"
                    value={companyValues.companySizeRange ?? ''}
                    onChange={handleCompanyInputChange('companySizeRange')}
                    disabled={!canEditCompany}
                  />
                </Field>

                <Field label="Website" icon={<Globe size={12} />}>
                  <input
                    className={inputClass}
                    name="website"
                    value={companyValues.website ?? ''}
                    onChange={handleCompanyInputChange('website')}
                    disabled={!canEditCompany}
                  />
                </Field>

                <Field label="Headquarters" icon={<MapPin size={12} />}>
                  <input
                    className={inputClass}
                    name="headquartersLocation"
                    value={companyValues.headquartersLocation ?? ''}
                    onChange={handleCompanyInputChange('headquartersLocation')}
                    disabled={!canEditCompany}
                  />
                </Field>

                <Field label="GST Number" icon={<Hash size={12} />}>
                  <input
                    className={inputClass}
                    name="gstNumber"
                    value={companyValues.gstNumber ?? ''}
                    onChange={handleCompanyInputChange('gstNumber')}
                    disabled={!canEditCompany}
                  />
                </Field>

                <Field label="Logo URL" icon={<Globe size={12} />}>
                  <input
                    className={inputClass}
                    name="companyLogoUrl"
                    value={companyValues.companyLogoUrl ?? ''}
                    onChange={handleCompanyInputChange('companyLogoUrl')}
                    disabled={!canEditCompany}
                  />
                </Field>

                <div className="md:col-span-2">
                  <Field label="About Company" icon={<FileText size={12} />}>
                    <textarea
                      className={`${inputClass} min-h-[110px] resize-y`}
                      name="description"
                      value={companyValues.description ?? ''}
                      onChange={handleCompanyInputChange('description')}
                      disabled={!canEditCompany}
                    />
                  </Field>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-slate-800/70 pt-5">
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-amber-400">
                    <Coins size={11} /> Credits: {companyValues.totalCredits ?? 0}
                  </span>
                  <span className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                    <CalendarDays size={12} /> Updated {formatDateTime(companyValues.updatedAt)}
                  </span>
                </div>
                {canEditCompany && (
                  <motion.button
                    onClick={handleSaveCompany}
                    disabled={savingCompany}
                    whileHover={{ translateY: -1 }}
                    whileTap={{ scale: 0.99 }}
                    className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 via-blue-600 to-indigo-600 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-white shadow-lg shadow-cyan-950/40 transition hover:shadow-cyan-500/20 disabled:opacity-60"
                  >
                    {savingCompany ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                    Save Company
                  </motion.button>
                )}
              </div>
            </>
          )}
        </SectionCard>

        {/* System metadata */}
        <SectionCard
          icon={<ShieldCheck size={18} />}
          title="System & Audit"
          subtitle="Read-only account metadata"
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Company Admin Privilege" icon={<ShieldCheck size={12} />}>
              <input
                className={inputClass}
                readOnly
                value={values.isCompanyAdminUser ? 'TRUE' : 'FALSE'}
              />
            </Field>
            <Field label="Account Status" icon={<ShieldCheck size={12} />}>
              <input className={inputClass} readOnly value={values.recruiterStatus ? 'ACTIVE' : 'DISABLED'} />
            </Field>
            <Field label="Record Initialized" icon={<CalendarDays size={12} />}>
              <input className={inputClass} readOnly value={formatDateTime(values.recruiterCreatedAt)} />
            </Field>
            <Field label="Last Synchronized" icon={<CalendarDays size={12} />}>
              <input className={inputClass} readOnly value={formatDateTime(values.recruiterUpdatedAt)} />
            </Field>
          </div>
        </SectionCard>

        {/* Danger zone */}
        <SectionCard
          icon={<Trash2 size={18} />}
          title="Account Access"
          subtitle="Deactivate your account or request deletion"
        >
          <p className="text-sm text-slate-400">
            Deactivating switches your access off until you reactivate it — nothing is deleted,
            and your company, its job posts and its credit balance are untouched. Permanent
            deletion is reviewed by an administrator first, and removes your recruiter profile
            along with your account.
          </p>
          <AccountAccessPanel
            className="mt-4"
            hint={
              values.isCompanyAdminUser
                ? 'You are a company admin — promote at least one other colleague to company admin before you can deactivate or delete your account.'
                : undefined
            }
          />
        </SectionCard>
      </div>
    </div>
  )
}

export default RecruiterProfile
